import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, Package, ShoppingBag, MessageCircle, ArrowLeftRight, 
  Eye, X, ChevronRight, ChevronDown, ChevronUp, CheckCircle, AlertCircle
} from 'lucide-react';
import ModelViewer from './ModelViewer';
import PieceActivityAndHolders from './PieceActivityAndHolders';
import { useWallet } from '../contexts/WalletContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useFavorites } from '../hooks/useFavorites';
import { purchasePiece } from '../utils/editionPurchase';
import { getActiveListing, canListPiece, canPurchaseListing, subscribeToListing, subscribeToPiece } from '../utils/resaleMarketplace';
import { getCategoryDisplayName } from '../utils/pieceUtils';
import { normalizePieces } from '../utils/pieceNormalizer';
import { getCapsuleForPiece } from '../utils/supabaseDatabase';
import { useWalletAction } from '../hooks/useWalletAction';
import { supabase } from '../lib/supabase';
import VerifiedBadge from './VerifiedBadge';

const FullPieceDetailModal = ({ 
  isOpen, 
  item, 
  onClose,
  onMakeOffer,
  onRequestTrade 
}) => {
  const navigate = useNavigate();
  
  // Wallet and context hooks
  const { isConnected, publicKey, wallet } = useWallet();
  const { solPrice } = useSolPrice();
  const { toggleFavorite, isFavorited } = useFavorites();
  const { withWalletCheck } = useWalletAction();
  
  // State
  const [modalViewMode, setModalViewMode] = useState('3d');
  const [threeDStatus, setThreeDStatus] = useState('loading');
  const [threeDError, setThreeDError] = useState(null);
  const [activityPanelOpen, setActivityPanelOpen] = useState(false);
  const [pieceCapsuleInfo, setPieceCapsuleInfo] = useState(null);
  const [activeListing, setActiveListing] = useState(null);
  const [listingLoading, setListingLoading] = useState(false);
  const [groupedPieceIds, setGroupedPieceIds] = useState([]);
  const [allEditions, setAllEditions] = useState([]);
  const [selectedEditionIndex, setSelectedEditionIndex] = useState(0);
  const [showEditionSelector, setShowEditionSelector] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  
  // Fetch capsule info and listing on mount
  useEffect(() => {
    if (isOpen && item) {
      console.log('🎨 FullPieceDetailModal opened with item:', {
        id: item.id,
        name: item.name,
        creator: item.creator,
        originalCreator: item.originalCreator,
        creators: item.creators,
        master_id: item.master_id,
        creator_id: item.creator_id,
        quantity: item.quantity,
        total_quantity: item.total_quantity,
        totalQuantity: item.totalQuantity,
        quantityType: typeof item.quantity,
        totalQuantityType: typeof item.total_quantity
      });
      
      // Fetch all editions of this piece (for holders display AND piece selector)
      const fetchAllEditions = async () => {
        try {
          let query = supabase
            .from('pieces')
            .select('*');
          
          let queryType = 'unknown';
          
          // Parse quantity values (they might be strings)
          const quantity = parseInt(item.quantity || item.total_quantity || 1);
          
          // Get creator_id from various possible sources
          const creatorId = item.creator_id || item.creatorId || item.creators?.id || item.creator?.id;
          
          // Strip any prefixes from the piece ID (supabase_, local_, etc)
          let cleanItemId = item.id;
          const itemIdString = String(item.id);
          if (itemIdString.startsWith('supabase_')) {
            cleanItemId = itemIdString.replace('supabase_', '');
            console.log('🔄 Stripped "supabase_" prefix from ID:', item.id, '->', cleanItemId);
          } else if (itemIdString.startsWith('local_')) {
            cleanItemId = itemIdString.replace('local_', '');
            console.log('🔄 Stripped "local_" prefix from ID:', item.id, '->', cleanItemId);
          }
          
          // Convert to integer if numeric
          if (!isNaN(cleanItemId)) {
            cleanItemId = parseInt(cleanItemId, 10);
          }
          
          console.log('📊 Checking edition fetch logic:', {
            hasmaster_id: !!item.master_id,
            quantity: quantity,
            hasName: !!item.name,
            creatorId: creatorId,
            hasCreatorId: !!creatorId,
            willFetchMultiple: quantity > 1,
            itemKeys: Object.keys(item),
            cleanItemId: cleanItemId
          });
          
          // Determine how to fetch editions:
          if (item.master_id) {
            // 1. If piece has master_id, fetch all pieces with same master_id
            console.log('🔍 Fetching all editions with master_id:', item.master_id);
            query = query.eq('master_id', item.master_id);
            queryType = 'master_id';
          } 
          else if (quantity > 1 && item.name && creatorId) {
            // 2. If piece has quantity > 1 but no master_id, fetch by name + creator_id to find all editions
            console.log('🔍 Piece has quantity > 1, fetching editions by name + creator:', { 
              name: item.name, 
              creator_id: creatorId,
              quantity: quantity 
            });
            query = query
              .eq('name', item.name)
              .eq('creator_id', creatorId);
            queryType = 'name+creator_id';
          }
          else {
            // 3. Single piece with no editions
            console.log('ℹ️ Single piece (no master_id, quantity = 1), showing only this piece:', cleanItemId);
            query = query.eq('id', cleanItemId);
            queryType = 'single';
          }
          
          // Order by created_at descending (newest first) to match Explore page
          query = query.order('created_at', { ascending: false });
          
          const { data: editions, error } = await query;
          
          console.log('📊 Query result:', { queryType, count: editions?.length, error });
          console.log('📊 Raw editions order from DB:', editions?.map(e => ({ id: e.id, mint: e.mint_address?.slice(0, 8) })));
          
          if (error) {
            console.error('Error fetching editions:', error);
            setGroupedPieceIds([item.id]);
            setAllEditions([item]);
            setSelectedEditionIndex(0);
          } else if (editions && editions.length > 0) {
            // Use database order (no sorting) to match Explore page behavior
            const pieceIds = editions.map(p => p.id);
            console.log('✅ Found editions (database order):', pieceIds);
            console.log('📊 Edition details:', editions.map((e, idx) => ({
              edition: `#${idx + 1}`,
              id: e.id,
              mint_address: e.mint_address?.slice(0, 8),
              holder: e.holder_wallet?.slice(0, 8)
            })));
            
            // Normalize editions to ensure consistent field names for modals
            const normalizedEditions = normalizePieces(editions);
            console.log('✅ Normalized editions for offer/trade modals');
            
            setGroupedPieceIds(pieceIds);
            setAllEditions(normalizedEditions);
            
            // Set selected index to the current piece OR the edition owned by current user
            let targetIndex = normalizedEditions.findIndex(p => p.id === item.id);
            
            // If user owns one of these editions, prioritize showing their owned edition
            if (publicKey) {
              const ownedIndex = normalizedEditions.findIndex(p => 
                p.holder_wallet === publicKey.toString()
              );
              if (ownedIndex >= 0) {
                targetIndex = ownedIndex;
                console.log('📍 User owns edition at index:', ownedIndex);
              }
            }
            
            console.log('📍 Setting selected index:', targetIndex, 'out of', normalizedEditions.length);
            setSelectedEditionIndex(targetIndex >= 0 ? targetIndex : 0);
          } else {
            console.log('⚠️ No editions found, using single piece');
            setGroupedPieceIds([item.id]);
            setAllEditions([item]);
            setSelectedEditionIndex(0);
          }
        } catch (error) {
          console.error('Error in fetchAllEditions:', error);
          setGroupedPieceIds([item.id]);
          setAllEditions([item]);
          setSelectedEditionIndex(0);
        }
      };
      
      fetchAllEditions();
      
      // Check if piece belongs to a capsule
      // Strip prefix from ID before fetching capsule info
      let cleanId = item.id;
      const idString = String(item.id);
      if (idString.startsWith('supabase_')) {
        cleanId = idString.replace('supabase_', '');
      } else if (idString.startsWith('local_')) {
        cleanId = idString.replace('local_', '');
      }
      if (!isNaN(cleanId)) {
        cleanId = parseInt(cleanId, 10);
      }
      
      getCapsuleForPiece(cleanId)
        .then(capsuleInfo => setPieceCapsuleInfo(capsuleInfo))
        .catch(error => {
          console.error('Error fetching capsule info:', error);
          setPieceCapsuleInfo(null);
        });
      
      // Check for active listing
      if (item.mint_address) {
        getActiveListing(item.mint_address)
          .then(listing => setActiveListing(listing))
          .catch(error => {
            console.error('Error fetching listing:', error);
            setActiveListing(null);
          });
      }
      
      // Set initial view mode
      const has3DModel = item?.previewUrl && 
        (item.previewUrl.includes('.glb') || item.previewUrl.includes('.gltf'));
      
      if (has3DModel) {
        setModalViewMode('3d');
        setThreeDStatus('loading');
        setThreeDError(null);
      } else {
        setModalViewMode('thumbnail');
        setThreeDStatus('error');
        setThreeDError('No 3D model available');
      }
    }
  }, [isOpen, item]);
  
  // Update active listing when edition changes
  useEffect(() => {
    if (allEditions.length > 0) {
      const currentPiece = allEditions[selectedEditionIndex];
      if (currentPiece?.mint_address) {
        setListingLoading(true);
        getActiveListing(currentPiece.mint_address)
          .then(listing => {
            setActiveListing(listing);
            setListingLoading(false);
          })
          .catch(error => {
            console.error('Error fetching listing:', error);
            setActiveListing(null);
            setListingLoading(false);
          });
      } else {
        setActiveListing(null);
      }
    }
  }, [selectedEditionIndex, allEditions]);
  
  // Real-time subscription for listing and piece changes
  useEffect(() => {
    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : item;
    
    if (!isOpen || !currentPiece?.mint_address) {
      return;
    }
    
    console.log('👁️ Setting up real-time subscriptions for:', currentPiece.mint_address);
    
    // Subscribe to listing changes
    const listingSubscription = subscribeToListing(currentPiece.mint_address, (payload) => {
      console.log('📡 Listing update received:', payload);
      
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        if (payload.new.status === 'active') {
          setActiveListing(payload.new);
          // Update the piece in allEditions array if applicable
          if (allEditions.length > 0) {
            setAllEditions(prev => prev.map((p, idx) => 
              idx === selectedEditionIndex 
                ? { ...p, listing_status: 'listed', listing_price: payload.new.price_sol }
                : p
            ));
          }
        } else if (payload.new.status === 'filled') {
          setActiveListing(null);
          if (allEditions.length > 0) {
            setAllEditions(prev => prev.map((p, idx) => 
              idx === selectedEditionIndex 
                ? { ...p, listing_status: 'sold', holder_wallet: payload.new.buyer_wallet }
                : p
            ));
          }
        } else if (payload.new.status === 'canceled') {
          // Listing canceled - remove buy button
          setActiveListing(null);
          if (allEditions.length > 0) {
            setAllEditions(prev => prev.map((p, idx) => 
              idx === selectedEditionIndex 
                ? { ...p, listing_status: 'unlisted', listing_price: null }
                : p
            ));
          }
        }
      } else if (payload.eventType === 'DELETE') {
        setActiveListing(null);
      }
    });
    
    // Subscribe to piece changes
    const pieceSubscription = subscribeToPiece(currentPiece.mint_address, (payload) => {
      console.log('📡 Piece update received:', payload);
      
      if (payload.eventType === 'UPDATE' && allEditions.length > 0) {
        setAllEditions(prev => prev.map((p, idx) => 
          idx === selectedEditionIndex 
            ? { ...p, ...payload.new }
            : p
        ));
      }
    });
    
    // Cleanup subscriptions
    return () => {
      console.log('🔌 Cleaning up subscriptions...');
      listingSubscription.unsubscribe();
      pieceSubscription.unsubscribe();
    };
  }, [isOpen, selectedEditionIndex, allEditions.length, item?.mint_address]);
  
  // Handle purchase from creator (primary sale)
  const handleBuyNow = withWalletCheck(async () => {
    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : item;
    try {
      setListingLoading(true);
      console.log('🛒 Purchasing from creator:', currentPiece.name);
      
      const result = await purchasePiece(
        wallet,
        currentPiece,
        currentPiece.listing_price || currentPiece.price
      );
      
      console.log('✅ Purchase successful:', result);
      setSuccessMessage(`Successfully purchased ${currentPiece.name}! 🎉`);
      setShowSuccessNotification(true);
      
      onClose();
      setTimeout(() => {
        setShowSuccessNotification(false);
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      setErrorMessage('Unable to complete purchase. Please try again.');
      setShowErrorNotification(true);
      setTimeout(() => setShowErrorNotification(false), 4000);
    } finally {
      setListingLoading(false);
    }
  }, 'purchase this piece');
  
  // Handle purchase from secondary marketplace
  const handlePurchaseListing = withWalletCheck(async () => {
    if (!activeListing) {
      setErrorMessage('No active listing found');
      setShowErrorNotification(true);
      setTimeout(() => setShowErrorNotification(false), 4000);
      return;
    }
    
    const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : item;
    try {
      setListingLoading(true);
      console.log('🛒 Purchasing from listing:', currentPiece.name);
      
      const result = await purchasePiece(
        wallet,
        currentPiece,
        activeListing.price_sol
      );
      
      console.log('✅ Purchase successful:', result);
      setSuccessMessage(`Successfully purchased ${currentPiece.name}! 🎉`);
      setShowSuccessNotification(true);
      
      onClose();
      setTimeout(() => {
        setShowSuccessNotification(false);
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('❌ Purchase failed:', error);
      setErrorMessage('Unable to complete purchase. Please try again.');
      setShowErrorNotification(true);
      setTimeout(() => setShowErrorNotification(false), 4000);
    } finally {
      setListingLoading(false);
    }
  }, 'purchase this piece');
  
  const handleViewCapsule = () => {
    if (pieceCapsuleInfo?.id) {
      navigate(`/capsule/${pieceCapsuleInfo.id}`);
      onClose();
    }
  };
  
  const handleAccountClick = () => {
    const creatorWallet = item?.originalCreator?.wallet || item?.creator?.wallet || item?.creator_wallet;
    if (creatorWallet) {
      navigate(`/creator/${creatorWallet}`);
      onClose();
    }
  };
  
  if (!isOpen || !item) return null;
  
  // Use selected edition if multiple editions exist, otherwise use the original item
  const currentPiece = allEditions.length > 0 ? allEditions[selectedEditionIndex] : item;
  const normalizedPiece = normalizePieces([currentPiece])[0];
  const hasMultipleEditions = allEditions.length > 1;
  
  console.log('🔍 Current render state:', {
    totalEditions: allEditions.length,
    selectedIndex: selectedEditionIndex,
    hasMultipleEditions,
    currentPieceId: currentPiece?.id,
    showSelector: hasMultipleEditions
  });
  
  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-[#111111] border border-[#2a2a2a] rounded-2xl max-w-5xl w-full max-h-[90vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
        style={{ overflow: 'visible' }}
      >
        <div className="flex flex-1 overflow-hidden rounded-2xl" style={{ position: 'relative', zIndex: 1 }}>
          {/* Left side - Thumbnail/3D Model */}
          <div className="w-1/2 p-6 overflow-y-auto">
            {/* 3D Model Toggle Buttons */}
            <div className="flex mb-4 bg-[#111111] rounded-xl p-1">
              <button
                onClick={() => setModalViewMode('3d')}
                className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  modalViewMode === '3d'
                    ? 'text-white bg-[#2a2a2a] shadow-lg' 
                    : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                }`}
              >
                3D Model
              </button>
              <button
                onClick={() => setModalViewMode('thumbnail')}
                className={`flex-1 py-2.5 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  modalViewMode === 'thumbnail'
                    ? 'text-white bg-[#2a2a2a] shadow-lg' 
                    : 'text-gray-400 hover:text-white hover:bg-[#0a0a0a]'
                }`}
              >
                Thumbnail
              </button>
            </div>
            
            <div className="rounded-xl flex items-center justify-center mb-1 overflow-hidden aspect-square bg-[#1a1a1a]">
              {modalViewMode === '3d' ? (
                (() => {
                  const modelBucket = item?.preview_bucket || item?.model_bucket;
                  const modelKey = item?.preview_key || item?.model_key;
                  const fallbackSrc = normalizedPiece?.previewUrl || normalizedPiece?.modelUrl;
                  
                  if (modelBucket && modelKey) {
                    const customOrientation = item?.customOrientation || null;
                    return (
                      <ModelViewer
                        modelBucket={modelBucket}
                        modelKey={modelKey}
                        posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                        orientation={customOrientation}
                        className="w-full h-full modal"
                        onLoad={() => setThreeDStatus('loaded')}
                        onError={(error) => {
                          setThreeDStatus('error');
                          setThreeDError(error.message || 'Failed to load 3D model');
                        }}
                      />
                    );
                  } else if (fallbackSrc) {
                    const customOrientation = item?.customOrientation || null;
                    return (
                      <ModelViewer
                        src={fallbackSrc}
                        posterUrl={normalizedPiece?.posterUrl || normalizedPiece?.thumbnailUrl || '/images/piece-placeholder.png'}
                        orientation={customOrientation}
                        className="w-full h-full modal"
                        onLoad={() => setThreeDStatus('loaded')}
                        onError={(error) => {
                          setThreeDStatus('error');
                          setThreeDError(error.message || 'Failed to load 3D model');
                        }}
                      />
                    );
                  } else {
                    return (
                      <div className="w-full h-full flex items-center justify-center bg-[#0a0a0a] rounded-xl">
                        <div className="text-center text-white p-6">
                          <div className="text-6xl mb-4">⚠️</div>
                          <div className="text-xl font-semibold mb-2">No 3D model available</div>
                          <div className="text-sm text-gray-400">This piece doesn't have a 3D model</div>
                        </div>
                      </div>
                    );
                  }
                })()
              ) : (
                normalizedPiece?.thumbnailUrl ? (
                  <img 
                    src={normalizedPiece.thumbnailUrl} 
                    alt={normalizedPiece.name || item.name}
                    className="w-full h-full object-cover rounded-xl"
                    onError={(e) => {
                      e.target.src = '/images/piece-placeholder.png';
                      e.target.onerror = null;
                    }}
                  />
                ) : item?.thumbnail?.dataUrl ? (
                  <img 
                    src={item.thumbnail.dataUrl} 
                    alt={item.name}
                    className="w-full h-full object-cover rounded-xl"
                    onError={(e) => {
                      e.target.src = '/images/piece-placeholder.png';
                      e.target.onerror = null;
                    }}
                  />
                ) : (
                  <img 
                    src="/images/piece-placeholder.png" 
                    alt="No preview available"
                    className="w-full h-full object-cover rounded-xl"
                  />
                )
              )}
            </div>

            {/* Creator Section */}
            <div className="space-y-4 mb-6">
              <div>
                <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2 px-1">
                  Original Creator
                </div>
                <button 
                  onClick={handleAccountClick}
                  className="w-full flex items-center gap-3 p-3 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-xl transition-colors group border border-[#1a1a1a]"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                    {(item?.originalCreator?.profilePicture || item?.creator?.profilePicture || item?.originalCreator?.profile_picture_url || item?.creator?.profile_picture_url) ? (
                      <img
                        src={item?.originalCreator?.profilePicture || item?.creator?.profilePicture || item?.originalCreator?.profile_picture_url || item?.creator?.profile_picture_url}
                        alt={item?.originalCreator?.username || item?.creator?.username || 'Creator'}
                        className="w-full h-full object-cover rounded-full"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {(item?.originalCreator?.username || item?.creator?.username || 'Creator').charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="text-white font-medium text-sm group-hover:text-gray-200 transition-colors truncate">
                        {item?.originalCreator?.username || item?.creator?.username || 'Original Creator'}
                      </div>
                      {(item?.originalCreator?.verified || item?.creator?.verified) && (
                        <VerifiedBadge 
                          size="sm"
                          verificationType={item?.originalCreator?.verificationType || item?.creator?.verificationType || 'none'}
                        />
                      )}
                    </div>
                    <div className="text-gray-400 text-xs mt-0.5">
                      {item?.originalCreator?.piecesCount || item?.creator?.piecesCount || '0'} pieces created
                    </div>
                  </div>
                  <div className="text-gray-400 group-hover:text-white transition-colors flex-shrink-0">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </div>
                </button>

                {/* Creator Royalty */}
                {item?.royalty_bps !== undefined && item?.royalty_bps !== null && (
                  <div className="mt-3 px-1">
                    <span className="inline-block px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded-md text-green-400 text-xs">
                      {(item.royalty_bps / 100).toFixed(1)}% Creator Royalty
                    </span>
                  </div>
                )}

                {/* Social Links */}
                {(() => {
                  const socialLinks = pieceCapsuleInfo 
                    ? {
                        website: pieceCapsuleInfo.website,
                        twitter: pieceCapsuleInfo.twitter,
                        instagram: pieceCapsuleInfo.instagram,
                        discord: pieceCapsuleInfo.discord
                      }
                    : (item?.social_links || {});
                  const hasSocialLinks = socialLinks.website || socialLinks.twitter || socialLinks.instagram || socialLinks.discord;
                  
                  if (!hasSocialLinks) return null;
                  
                  return (
                    <div className="flex items-center gap-2 mt-3 px-1">
                      {socialLinks.website && (
                        <a
                          href={socialLinks.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Website"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.twitter && (
                        <a
                          href={socialLinks.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="X (Twitter)"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.instagram && (
                        <a
                          href={socialLinks.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Instagram"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400 hover:text-white transition-colors">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        </a>
                      )}
                      {socialLinks.discord && (
                        <a
                          href={socialLinks.discord}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-[#0a0a0a] hover:bg-[#1a1a1a] rounded-lg transition-colors border border-[#1a1a1a] hover:border-[#2a2a2a]"
                          title="Discord"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-400 hover:text-white transition-colors">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Right side - Details */}
          <div className="w-1/2 p-6 border-l border-[#2a2a2a] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold text-white">
                    {item?.name}
                  </h2>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(item.id);
                    }}
                    className="p-1.5 transition-all duration-300 hover:scale-110 active:scale-95"
                    title={isFavorited(item.id) ? "Remove from favorites" : "Add to favorites"}
                  >
                    <Heart 
                      size={20} 
                      className={`transition-all duration-300 ${
                        isFavorited(item.id)
                          ? 'text-red-500 fill-red-500'
                          : 'text-gray-400 hover:text-red-500'
                      }`} 
                    />
                  </button>
                </div>
                {/* Tags */}
                <div className="flex items-center gap-2 flex-wrap">
                  {item?.tags && item.tags.length > 0 ? (
                    item.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="bg-gray-600 text-gray-200 px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">No tags</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setActivityPanelOpen(!activityPanelOpen)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                  activityPanelOpen 
                    ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-[#3a3a3a]' 
                    : 'bg-[#1a1a1a] text-gray-300 hover:bg-[#2a2a2a] hover:text-white border border-[#2a2a2a]'
                }`}
              >
                Activity
              </button>
            </div>

            <div className="space-y-5">
              {/* Edition Selector - Only show if multiple editions */}
              {hasMultipleEditions && (
                <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                  <div className="mb-2">
                    <span className="text-gray-400 text-sm font-semibold">Select Piece</span>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEditionSelector(!showEditionSelector);
                    }}
                    className="w-full flex items-center justify-between p-3 bg-[#111111] hover:bg-[#1a1a1a] rounded-lg transition-all border border-[#2a2a2a]"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-medium">Piece #{selectedEditionIndex + 1}</span>
                      <span className="text-xs text-gray-500 font-mono">
                        {currentPiece.mint_address?.slice(0, 8)}...
                      </span>
                    </div>
                    {showEditionSelector ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </button>

                  {showEditionSelector && (
                    <div className="mt-2 max-h-40 overflow-y-auto bg-[#111111] rounded-lg border border-[#2a2a2a]">
                      {(() => {
                        console.log('🎯 Rendering dropdown with allEditions:', allEditions.map((e, i) => ({ index: i, id: e.id, piece: `#${i+1}` })));
                        return null;
                      })()}
                      {allEditions.map((piece, index) => (
                        <button
                          key={piece.id || index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEditionIndex(index);
                            setShowEditionSelector(false);
                          }}
                          className={`w-full flex items-center justify-between p-2.5 hover:bg-[#1a1a1a] transition-all text-left ${
                            index === selectedEditionIndex ? 'bg-[#1a1a1a]' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-white text-sm">Piece #{index + 1}</span>
                            <span className="text-xs text-gray-500 font-mono">
                              {piece.mint_address?.slice(0, 6)}...{piece.mint_address?.slice(-4)}
                            </span>
                          </div>
                          {index === selectedEditionIndex && (
                            <span className="text-green-400 text-xs font-medium">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Category and Color */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span className="text-gray-300 text-sm font-medium">
                      {getCategoryDisplayName(currentPiece?.category) || 'Uncategorized'}
                    </span>
                  </div>
                  {currentPiece?.color && (
                    <div className="text-gray-300 text-sm">
                      <span className="text-gray-500">Color: </span>
                      <span className="capitalize font-medium">{currentPiece.color}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                <h3 className="text-white font-semibold mb-2 text-sm">Description</h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {currentPiece?.description || 'A premium digital fashion piece with modern styling and high-quality design.'}
                </p>
              </div>

              {/* Price */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                <h3 className="text-white font-semibold mb-3 text-sm">Pricing</h3>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Price</span>
                    <div className="text-green-400 font-bold text-xl">
                      {(() => {
                        const isOwnedByCurrentUser = publicKey && currentPiece?.holder_wallet === publicKey.toString();
                        const isListed = currentPiece?.listing_status === 'listed';
                        
                        let displayPrice;
                        // If user owns this piece
                        if (isOwnedByCurrentUser) {
                          // If they've listed it, show THEIR listing price
                          if (isListed && currentPiece?.listing_price) {
                            displayPrice = currentPiece.listing_price;
                          }
                          // If not listed, show purchase price
                          else if (!isListed && currentPiece?.last_sale_price_lamports) {
                            displayPrice = currentPiece.last_sale_price_lamports / 1_000_000_000;
                          }
                          else {
                            displayPrice = currentPiece?.price || 0;
                          }
                        } else {
                          // For pieces not owned by user, show lowest available price
                          displayPrice = currentPiece?.lowestPrice ?? currentPiece?.listing_price ?? currentPiece?.price;
                        }
                        
                        // Format price to remove trailing zeros
                        const formatted = typeof displayPrice === 'number' 
                          ? displayPrice.toFixed(4).replace(/\.?0+$/, '')
                          : displayPrice;
                        return formatted;
                      })()} SOL
                    </div>
                  </div>
                  <div className="text-right text-gray-500 text-sm">
                    ≈ ${(() => {
                      const isOwnedByCurrentUser = publicKey && currentPiece?.holder_wallet === publicKey.toString();
                      const isListed = currentPiece?.listing_status === 'listed';
                      
                      let displayPrice;
                      if (isOwnedByCurrentUser) {
                        if (isListed && currentPiece?.listing_price) {
                          displayPrice = currentPiece.listing_price;
                        } else if (!isListed && currentPiece?.last_sale_price_lamports) {
                          displayPrice = currentPiece.last_sale_price_lamports / 1_000_000_000;
                        } else {
                          displayPrice = currentPiece?.price || 0;
                        }
                      } else {
                        displayPrice = currentPiece?.lowestPrice ?? currentPiece?.listing_price ?? currentPiece?.price;
                      }
                      return ((displayPrice || 0) * solPrice).toFixed(2);
                    })()} USD
                  </div>
                </div>
              </div>

              {/* Scarcity */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#1a1a1a]">
                <div className="text-gray-400 text-sm mb-1">Scarcity</div>
                <div className="text-white font-semibold text-lg">
                  {allEditions.length > 1 ? allEditions.length : (currentPiece?.quantity || 1)}
                </div>
              </div>

              {/* Dropped Date */}
              <div className="text-gray-500 text-sm pt-2">
                Dropped {currentPiece?.created_at ? new Date(currentPiece.created_at).toLocaleDateString('en-US', { 
                  year: '2-digit', 
                  month: '2-digit', 
                  day: '2-digit' 
                }) : currentPiece?.createdAt ? new Date(currentPiece.createdAt).toLocaleDateString('en-US', { 
                  year: '2-digit', 
                  month: '2-digit', 
                  day: '2-digit' 
                }) : 'Recently'}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-4 pb-2">
                {(() => {
                  const isOwner = isConnected && publicKey && 
                    currentPiece?.holder_wallet === publicKey.toString();
                  
                  const creatorWallet = currentPiece?.creator_wallet || 
                                       currentPiece?.creators?.wallet ||
                                       currentPiece?.originalCreator?.wallet;
                  const isOwnedByCreator = currentPiece?.holder_wallet === creatorWallet;
                  
                  const listEligibility = canListPiece(wallet, currentPiece);
                  const purchaseEligibility = canPurchaseListing(wallet, activeListing, currentPiece);
                  
                  return (
                    <>
                      {/* OWNERSHIP BADGE */}
                      {isOwner && (
                        <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-xl text-center border border-green-600/30 mb-2">
                          <span className="text-sm font-medium">✓ You own this piece</span>
                        </div>
                      )}
                      
                      {/* PRIMARY SALE: Buy directly from creator */}
                      {!isOwner && isOwnedByCreator && 
                       currentPiece?.mint_address && 
                       !activeListing &&
                       currentPiece?.listing_status !== 'sold' && (
                        <button
                          onClick={handleBuyNow}
                          className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25"
                        >
                          Buy Now - {(currentPiece?.listing_price || currentPiece?.price || 0).toFixed(4)} SOL
                        </button>
                      )}
                      
                      {/* SECONDARY SALE: Purchase listed piece */}
                      {!isOwner && activeListing && activeListing.status === 'active' && 
                       currentPiece?.listing_status === 'listed' && (
                        <button
                          onClick={handlePurchaseListing}
                          disabled={listingLoading || !purchaseEligibility.canPurchase}
                          className="w-full px-4 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 font-semibold text-sm shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
                          title={!purchaseEligibility.canPurchase ? purchaseEligibility.reason : ''}
                        >
                          {listingLoading ? 'Processing...' : `Buy Now - ${activeListing.price_sol} SOL`}
                        </button>
                      )}
                      
                      {/* Sold status */}
                      {currentPiece?.listing_status === 'sold' && !isOwner && (
                        <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                          ✅ Sold
                        </div>
                      )}
                      
                      {/* Unlisted status */}
                      {currentPiece?.listing_status === 'unlisted' && !isOwner && !isOwnedByCreator && (
                        <div className="w-full px-4 py-3.5 bg-gray-800 text-gray-400 rounded-xl text-center font-semibold text-sm">
                          Not for Sale
                        </div>
                      )}
                    </>
                  );
                })()}
                
                {/* Make Offer and Request Trade Buttons */}
                {(() => {
                  const isOwner = isConnected && publicKey && 
                    currentPiece?.holder_wallet === publicKey.toString();
                  
                  if (isOwner) return null;
                  
                  return (
                    <div className="flex gap-3">
                      <button 
                        onClick={() => onMakeOffer && onMakeOffer(currentPiece)}
                        className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a]"
                      >
                        Make Offer
                      </button>
                      <button 
                        onClick={() => onRequestTrade && onRequestTrade(currentPiece)}
                        className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-semibold text-sm border border-[#3a3a3a] flex items-center justify-center gap-2"
                      >
                        <ArrowLeftRight size={16} />
                        Request Trade
                      </button>
                    </div>
                  );
                })()}

                {/* View Entire Capsule Button */}
                {pieceCapsuleInfo && (
                  <div className="pt-3">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewCapsule();
                      }}
                      className="w-full px-4 py-2.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#3a3a3a] hover:border-[#4a4a4a] text-gray-300 hover:text-white rounded-lg transition-all duration-200 font-medium text-sm flex items-center justify-center gap-2"
                    >
                      <Package size={14} />
                      View entire capsule
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Side Panel */}
        <div 
          className="absolute top-0 left-full h-full w-80 bg-[#0a0a0a] border-l border-[#2a2a2a] rounded-r-2xl transition-all duration-300 ease-in-out shadow-2xl"
          style={{ 
            transform: activityPanelOpen ? 'translateX(0)' : 'translateX(-100%)',
            zIndex: activityPanelOpen ? 50 : 0,
            opacity: activityPanelOpen ? 1 : 0,
            visibility: activityPanelOpen ? 'visible' : 'hidden',
            pointerEvents: activityPanelOpen ? 'auto' : 'none'
          }}
        >
          <div className="h-full flex flex-col p-5">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-[#2a2a2a]">
              <h3 className="text-base font-bold text-white">Activity</h3>
              <button
                onClick={() => setActivityPanelOpen(false)}
                className="p-1.5 hover:bg-[#1a1a1a] rounded-lg transition-colors"
                title="Close Activity Panel"
              >
                <ChevronRight size={18} className="text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <PieceActivityAndHolders
                pieceId={currentPiece?.id}
                pieceName={currentPiece?.name}
                basePrice={parseFloat(currentPiece?.price) || 0}
                solPrice={solPrice || 150}
                totalQuantity={groupedPieceIds.length > 0 ? groupedPieceIds.length : (currentPiece?.total_quantity || currentPiece?.quantity || 1)}
                groupedPieceIds={groupedPieceIds.length > 0 ? groupedPieceIds : null}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Success Notification Toast */}
      {showSuccessNotification && (
        <div className="fixed top-4 right-4 z-[70] animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="bg-green-600 border border-green-500/30 rounded-xl p-4 shadow-lg max-w-sm">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle size={14} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">{successMessage}</p>
              </div>
              <button
                onClick={() => setShowSuccessNotification(false)}
                className="text-white/70 hover:text-white transition-colors flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Notification Toast */}
      {showErrorNotification && (
        <div className="fixed top-4 right-4 z-[70] animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="bg-red-600 border border-red-500/30 rounded-xl p-4 shadow-lg max-w-sm">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertCircle size={14} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">{errorMessage}</p>
              </div>
              <button
                onClick={() => setShowErrorNotification(false)}
                className="text-white/70 hover:text-white transition-colors flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FullPieceDetailModal;

